import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import BareInput from "components/DesignSystem/Input/BareInput";
import Modal from "components/DesignSystem/Modal/Modal";
import {
  CRM_EXPORT_FORMAT_OPTIONS,
  LARGE_EXPORT_ROW_WARNING_THRESHOLD,
} from "constants/crmFieldExport";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useExportCrmFieldsMutation,
  useGetCrmExportTemplatesQuery,
  useGetCrmFilterFieldsQuery,
  useSaveCrmExportTemplateMutation,
} from "store/apis/crmFields";
import {
  CrmExportFormat,
  CrmExportTemplate,
  CrmFilterField,
} from "types/Models/crmFields";
import { CrmAvailableFieldRow } from "./CrmAvailableFieldRow";
import { CrmExportPreviewTable } from "./CrmExportPreviewTable";
import { CrmSelectedFieldRow } from "./CrmSelectedFieldRow";

type CrmFieldExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appliedFieldKeys: string[];
  onExported: (fieldKeys: string[]) => void;
  /** Whatever the group list currently has applied (crm_filters, search_term, tag_filter, ...),
   * straight from the page's own URLSearchParams, so the export matches what's on screen. */
  filterParams: Record<string, unknown>;
};

const EMPTY_FIELDS: CrmFilterField[] = [];
const EMPTY_TEMPLATES: CrmExportTemplate[] = [];
const SELECT_PORTAL_TARGET = document.body;

export const CrmFieldExportModal = ({
  isOpen,
  onClose,
  appliedFieldKeys,
  onExported,
  filterParams,
}: CrmFieldExportModalProps) => {
  const [draftFieldKeys, setDraftFieldKeys] =
    useState<string[]>(appliedFieldKeys);
  const [search, setSearch] = useState("");
  const [exportFormat, setExportFormat] = useState<CrmExportFormat>("xlsx");
  const [hasSeededDefault, setHasSeededDefault] = useState(false);
  const [pendingRowCount, setPendingRowCount] = useState<number | null>(null);
  const [templateName, setTemplateName] = useState("");

  const {
    data: fieldsData,
    isLoading,
    isSuccess,
    isError,
  } = useGetCrmFilterFieldsQuery(undefined, { skip: !isOpen });
  const { data: templatesData } = useGetCrmExportTemplatesQuery(undefined, {
    skip: !isOpen,
  });
  const [triggerExport, { isLoading: isExporting }] =
    useExportCrmFieldsMutation();
  const [saveTemplate, { isLoading: isSavingTemplate }] =
    useSaveCrmExportTemplateMutation();

  const fields = fieldsData || EMPTY_FIELDS;
  const templates = templatesData?.templates ?? EMPTY_TEMPLATES;

  // Keyed lookups recompute only when the catalog itself changes, not on every keystroke/drag.
  const fieldByKey = useMemo(
    () => new Map(fields.map((field) => [field.field_key, field])),
    [fields]
  );

  // This array's identity only changes when the picked keys (or the catalog) actually change,
  // so the memoized preview table skips re-rendering while the user searches or picks a template.
  const selectedFields = useMemo(
    () =>
      draftFieldKeys
        .map((key) => fieldByKey.get(key))
        .filter((field): field is CrmFilterField => Boolean(field)),
    [draftFieldKeys, fieldByKey]
  );

  const availableFields = useMemo(() => {
    const selectedKeys = new Set(draftFieldKeys);
    const needle = search.trim().toLowerCase();
    return fields.filter((field) => {
      if (selectedKeys.has(field.field_key)) return false;
      if (needle && !field.label.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [fields, draftFieldKeys, search]);

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        value: template.uuid,
        label: template.is_personal ? `${template.name} (mine)` : template.name,
      })),
    [templates]
  );

  useEffect(() => {
    if (hasSeededDefault || appliedFieldKeys.length > 0 || !templatesData) {
      return;
    }
    setHasSeededDefault(true);
    // Guard on current draft state, not just the initial prop - the templates query can resolve
    // after the user has already picked a field by hand, and that pick must never be clobbered
    setDraftFieldKeys((current) => {
      if (current.length > 0) {
        return current;
      }
      const defaultTemplate = templatesData.templates.find(
        (template) => template.is_system_default
      );
      return defaultTemplate ? defaultTemplate.field_keys : current;
    });
  }, [templatesData, hasSeededDefault, appliedFieldKeys]);

  const addField = useCallback((fieldKey: string) => {
    setDraftFieldKeys((current) => [...current, fieldKey]);
  }, []);

  const removeField = useCallback((fieldKey: string) => {
    setDraftFieldKeys((current) => current.filter((key) => key !== fieldKey));
  }, []);

  const moveField = useCallback((fromIndex: number, toIndex: number) => {
    setDraftFieldKeys((current) => {
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleTemplateSelect = (option: unknown) => {
    if (!(option && typeof option === "object" && "value" in option)) {
      return;
    }
    const template = templates.find((item) => item.uuid === option.value);
    if (template) {
      setDraftFieldKeys(template.field_keys);
    }
  };

  const handleFormatSelect = (option: unknown) => {
    if (option && typeof option === "object" && "value" in option) {
      setExportFormat(option.value as CrmExportFormat);
    }
  };

  const clearAll = () => {
    setDraftFieldKeys([]);
  };

  const resetAndClose = () => {
    setDraftFieldKeys(appliedFieldKeys);
    setPendingRowCount(null);
    setTemplateName("");
    onClose();
  };

  const runExport = async () => {
    const result = await triggerExport({
      exportFields: draftFieldKeys,
      exportFormat,
      filterParams,
    }).unwrap();
    if ("download_url" in result) {
      window.open(result.download_url, "_blank");
    }
    onExported(draftFieldKeys);
    setPendingRowCount(null);
    onClose();
  };

  const handleExportClick = async () => {
    const preview = await triggerExport({
      exportFields: draftFieldKeys,
      exportFormat,
      preview: true,
      filterParams,
    }).unwrap();

    if (
      "row_count" in preview &&
      preview.row_count > LARGE_EXPORT_ROW_WARNING_THRESHOLD
    ) {
      setPendingRowCount(preview.row_count);
      return;
    }
    await runExport();
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      return;
    }
    await saveTemplate({
      name: templateName.trim(),
      field_keys: draftFieldKeys,
    }).unwrap();
    setTemplateName("");
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    resetAndClose();
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content size="xxl" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Export CRM fields</Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body className="t-flex t-min-h-0 t-flex-col">
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isError={isError}
            isEmpty={fields.length === 0}
          >
            <Async.Empty>
              <div className="t-py-6 t-text-center t-text-body-sm t-text-text-30">
                No CRM fields are configured yet.
              </div>
            </Async.Empty>

            <Async.ErrorHandler>
              <div className="t-py-6 t-text-center t-text-body-sm t-text-text-30">
                Could not load CRM fields. Please try again.
              </div>
            </Async.ErrorHandler>

            <Async.Success>
              <div className="t-grid t-h-[60vh] t-min-h-0 t-grid-cols-2 t-overflow-hidden t-rounded t-border t-border-solid t-border-neutral-0">
                <div className="t-flex t-min-h-0 t-min-w-0 t-flex-col t-border-0 t-border-r t-border-solid t-border-neutral-0 t-bg-surface-grey">
                  <div className="t-flex t-shrink-0 t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-neutral-0 t-px-4 t-py-2 t-text-body-sm t-text-text-30">
                    <span>
                      <b className="t-text-text-60">{selectedFields.length}</b>{" "}
                      columns selected
                    </span>
                    <span className="t-rounded-full t-bg-surface-purple t-px-2 t-py-0.5 t-text-body-sm t-text-purple">
                      Sample data
                    </span>
                  </div>
                  <div className="t-min-h-0 t-flex-1">
                    <CrmExportPreviewTable fields={selectedFields} />
                  </div>
                </div>

                <div className="t-flex t-min-h-0 t-min-w-0 t-flex-col t-gap-4 t-overflow-auto t-p-4">
                  {templateOptions.length > 0 && (
                    <div className="t-w-full">
                      <Combobox
                        withForm={false}
                        size="small"
                        name="export-template"
                        placeholder="Start from a template"
                        menuPortalTarget={SELECT_PORTAL_TARGET}
                        isClearable={false}
                        options={templateOptions}
                        value={null}
                        onChange={handleTemplateSelect}
                      />
                    </div>
                  )}

                  <div className="t-grid t-grid-cols-2 t-gap-3">
                    <div className="t-flex t-max-h-[280px] t-flex-col t-rounded t-border t-border-solid t-border-neutral-0">
                      <div className="t-shrink-0 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-bg-surface-grey t-px-3 t-py-2 t-text-body-sm t-font-semibold t-uppercase t-tracking-wide t-text-text-30">
                        Available
                      </div>
                      <div className="t-shrink-0 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-p-2">
                        <BareInput
                          name="export-field-search"
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Search fields…"
                          customSize="small"
                        />
                      </div>
                      <div
                        className="t-min-h-0 t-flex-1 t-overflow-auto"
                        data-testid="available-fields-list"
                      >
                        {availableFields.length === 0 && (
                          <p className="t-px-3 t-py-3 t-text-body-sm t-text-text-30">
                            {search
                              ? `No fields match "${search}".`
                              : "Every field has been added."}
                          </p>
                        )}
                        {availableFields.map((field) => (
                          <CrmAvailableFieldRow
                            key={field.field_key}
                            field={field}
                            onAdd={addField}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="t-flex t-max-h-[280px] t-flex-col t-rounded t-border t-border-solid t-border-neutral-0">
                      <div className="t-shrink-0 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-bg-surface-grey t-px-3 t-py-2 t-text-body-sm t-font-semibold t-uppercase t-tracking-wide t-text-text-30">
                        Selected · drag to reorder
                      </div>
                      <div
                        className="t-min-h-0 t-flex-1 t-overflow-auto"
                        data-testid="selected-fields-list"
                      >
                        {selectedFields.length === 0 && (
                          <p className="t-px-3 t-py-3 t-text-body-sm t-text-text-30">
                            Add fields from the left.
                          </p>
                        )}
                        {selectedFields.map((field, index) => (
                          <CrmSelectedFieldRow
                            key={field.field_key}
                            field={field}
                            index={index}
                            onRemove={removeField}
                            onMove={moveField}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="t-flex t-items-end t-gap-3">
                    <div className="t-w-48">
                      <div className="t-mb-1 t-text-body-sm t-text-text-60">
                        Save columns as a template
                      </div>
                      <BareInput
                        name="export-template-name"
                        value={templateName}
                        onChange={(event) =>
                          setTemplateName(event.target.value)
                        }
                        placeholder="Template name"
                        customSize="small"
                      />
                    </div>
                    <Button
                      type="button"
                      size="small"
                      isLoading={isSavingTemplate}
                      disabled={
                        !templateName.trim() || draftFieldKeys.length === 0
                      }
                      onClick={handleSaveTemplate}
                    >
                      Save as template
                    </Button>
                  </div>

                  <div className="t-w-48">
                    <Combobox
                      withForm={false}
                      size="small"
                      name="export-format"
                      menuPortalTarget={SELECT_PORTAL_TARGET}
                      isClearable={false}
                      options={CRM_EXPORT_FORMAT_OPTIONS}
                      value={CRM_EXPORT_FORMAT_OPTIONS.find(
                        ({ value }) => value === exportFormat
                      )}
                      onChange={handleFormatSelect}
                    />
                  </div>

                  {pendingRowCount !== null && (
                    <div className="t-rounded t-bg-orange-20 t-p-3">
                      <HelperText variant="warning">
                        This will export {pendingRowCount} rows.
                      </HelperText>
                      <Button
                        type="button"
                        size="small"
                        customType="primary"
                        isLoading={isExporting}
                        onClick={runExport}
                      >
                        Continue export
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>

        <Modal.Footer>
          <div className="t-flex t-w-full t-items-center t-justify-between">
            <Button type="button" customType="link" onClick={clearAll}>
              Clear all
            </Button>

            <div className="t-flex t-gap-3">
              <Button type="button" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button
                type="button"
                customType="primary"
                isLoading={isExporting}
                disabled={draftFieldKeys.length === 0}
                onClick={handleExportClick}
              >
                Export
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
